/**
 * Live updates inside a company workspace (developer.md ADR-026). One tab per company polls the
 * server's outbox (`LiveFeed`) and shares what it hears with the company's other tabs over a
 * BroadcastChannel; the Web Locks API decides which tab that is and hands the job on when it closes.
 * Without either API, every tab polls on its own.
 *
 * Each tab dedupes events by id and invalidates the queries the topic registry (`topics.ts`) names,
 * so visible screens refetch. A `reset` invalidates every query of the company.
 * The status (connecting, live, reconnecting, offline) is shown in the shell footer.
 */
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

import { api, unwrap } from "@/api/client";
import { isPreferenceSavePending } from "@/hooks/usePreferences";
import { cx } from "@/lib/cx";

import { LiveFeed, SeenIds, VISIBLE_MS, type FeedPage, type LiveStatus } from "./feed";
import { staleKeys, type LiveEvent } from "./topics";

const LiveStatusContext = createContext<LiveStatus | null>(null);

/** A tab that listens says it is visible this often, so the polling tab keeps the fast pace. */
const ACTIVE_PING_MS = VISIBLE_MS;
const ACTIVE_WINDOW_MS = 3 * VISIBLE_MS;

type Message =
  | { type: "events"; events: LiveEvent[] }
  | { type: "reset" }
  | { type: "status"; status: LiveStatus }
  // A listening tab is visible (every few seconds): keep the fast pace even if the polling tab is hidden.
  | { type: "active" }
  // A listening tab got focus or came online: poll now.
  | { type: "wake" };

export function useLiveStatus(): LiveStatus | null {
  return useContext(LiveStatusContext);
}

function fetchPage(slug: string, after: number | null): Promise<FeedPage> {
  return unwrap(
    api.GET("/api/t/{tenant_slug}/events/", {
      params: { path: { tenant_slug: slug }, query: after === null ? {} : { after } },
    }),
  );
}

/** Apply events (or a reset) to this tab's query cache. */
export function applyLiveEvents(qc: QueryClient, slug: string, events: LiveEvent[]) {
  for (const queryKey of staleKeys(events, slug)) void qc.invalidateQueries({ queryKey });
  if (events.some((e) => e.topic === "preference") && !isPreferenceSavePending(`tenant:${slug}`)) {
    void qc.invalidateQueries({ queryKey: ["tenant", slug, "preferences"] });
  }
}

export function applyLiveReset(qc: QueryClient, slug: string) {
  void qc.invalidateQueries({ queryKey: ["tenant", slug] });
}

const visible = () => typeof document === "undefined" || document.visibilityState !== "hidden";

export function LiveUpdatesProvider({ slug, children }: { slug: string; children: ReactNode }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<LiveStatus>("connecting");

  useEffect(() => {
    const seen = new SeenIds();
    const deliver = (events: LiveEvent[]) => {
      const fresh = seen.fresh(events);
      if (fresh.length) applyLiveEvents(qc, slug, fresh);
    };
    const reset = () => applyLiveReset(qc, slug);
    const channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(`kshop-live:${slug}`);
    const locks = typeof navigator === "undefined" ? undefined : navigator.locks;
    const shared = Boolean(channel && locks);
    const post = (message: Message) => {
      if (shared) channel!.postMessage(message);
    };
    let feed: LiveFeed | null = null;
    let lastActiveElsewhere = 0;
    let stopped = false;

    const lead = () => {
      if (stopped) return;
      feed = new LiveFeed({
        fetchPage: (after) => fetchPage(slug, after),
        onEvents: (events) => {
          deliver(events);
          post({ type: "events", events });
        },
        onReset: () => {
          reset();
          post({ type: "reset" });
        },
        onStatus: (next) => {
          setStatus(next);
          post({ type: "status", status: next });
        },
        isActive: () => visible() || Date.now() - lastActiveElsewhere < ACTIVE_WINDOW_MS,
        storageKey: `kshop:live:cursor:${slug}`,
      });
      feed.start();
    };

    if (channel && shared) {
      channel.onmessage = (event: MessageEvent<Message>) => {
        const message = event.data;
        if (message.type === "active") {
          lastActiveElsewhere = Date.now();
          feed?.poke(VISIBLE_MS, true); // only if the last poll is older than the fast pace
        } else if (message.type === "wake") {
          lastActiveElsewhere = Date.now();
          feed?.poke();
        } else if (!feed) {
          // Listening tab: the polling tab tells us what changed.
          if (message.type === "events") deliver(message.events);
          else if (message.type === "reset") reset();
          else if (message.type === "status") setStatus(message.status);
        }
      };
    }

    // Leadership: the tab holding the lock polls; the lock passes on when that tab closes.
    const controller = new AbortController();
    let release: (() => void) | null = null;
    if (shared) {
      locks!
        .request(`kshop-live:${slug}`, { signal: controller.signal }, () => {
          lead();
          return new Promise<void>((resolve) => {
            release = resolve;
          });
        })
        .catch(() => {
          /* aborted on unmount */
        });
    } else {
      lead();
    }

    const wake = () => {
      if (!visible()) return;
      if (feed) feed.poke();
      else post({ type: "wake" });
    };
    const ping = setInterval(() => {
      if (!feed && visible()) post({ type: "active" });
    }, ACTIVE_PING_MS);
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("focus", wake);
    window.addEventListener("online", wake);
    const offline = () => {
      if (feed) feed.poke();
      setStatus("offline");
    };
    window.addEventListener("offline", offline);

    return () => {
      stopped = true;
      feed?.stop();
      controller.abort();
      release?.();
      clearInterval(ping);
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("focus", wake);
      window.removeEventListener("online", wake);
      window.removeEventListener("offline", offline);
      channel?.close();
    };
  }, [qc, slug]);

  return <LiveStatusContext.Provider value={status}>{children}</LiveStatusContext.Provider>;
}

const LABELS: Record<LiveStatus, { text: string; title: string; dot: string }> = {
  connecting: { text: "Connecting", title: "Connecting to live updates", dot: "bg-fg-subtle" },
  live: { text: "Live", title: "Live: changes by others appear within seconds", dot: "bg-success" },
  reconnecting: { text: "Reconnecting", title: "Reconnecting: changes by others may appear late", dot: "bg-warning" },
  offline: { text: "Offline", title: "Offline: changes appear when the connection is back", dot: "bg-danger" },
};

/** The footer's connection indicator: a dot and a word (never colour alone), announced politely. */
export function LiveStatusIndicator({ className }: { className?: string }) {
  const status = useLiveStatus();
  if (!status) return null;
  const label = LABELS[status];
  return (
    <span role="status" title={label.title} className={cx("inline-flex items-center gap-1.5", className)}>
      <span aria-hidden className={cx("size-1.5 rounded-full", label.dot)} />
      <span>
        <span className="sr-only">Live updates: </span>
        {label.text}
      </span>
    </span>
  );
}
